import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { checkSupabaseConnection, supabase } from '../lib/supabase';

export default function SignIn() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isCheckingConnection, setIsCheckingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const [resetError, setResetError] = useState('');

  const navigate = useNavigate();
  const { signIn } = useAuth();

  const handleCheckConnection = async () => {
    setIsCheckingConnection(true);
    setConnectionStatus(null);
    try {
      const status = await checkSupabaseConnection();
      setConnectionStatus(status);
    } catch (err) {
      setConnectionStatus({
        status: 'error',
        message: 'Failed to check connection',
        details: err?.message
      });
    } finally {
      setIsCheckingConnection(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError('');

    try {
      console.log('Attempting signin for:', email);
      await signIn(email, password);
      console.log('Signin successful, redirecting...');
      navigate('/dashboard');
    } catch (err) {
      console.error('Signin error:', err);
      let errorMessage = 'Failed to sign in. Please try again.';
      
      const msg = err?.message || '';

      if (
        msg?.includes('fetch failed') ||
        msg?.includes('Failed to fetch') ||
        msg?.includes('NetworkError') ||
        err?.status === 503 ||
        err?.status === 502
      ) {
        errorMessage = 'SUPABASE_PAUSED';
      } else if (msg?.includes('Authentication schema error') || msg?.includes('Database error querying schema')) {
        errorMessage = 'SCHEMA_ERROR';
      } else if (msg?.includes('Invalid login credentials') || msg?.includes('Invalid email or password')) {
        errorMessage = 'Invalid email or password. Please check your credentials and try again, or use "Forgot password?" to reset your password.';
      } else if (msg?.includes('Email not confirmed')) {
        errorMessage = 'Please confirm your email address before signing in.';
      } else if (msg) {
        errorMessage = msg;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e?.preventDefault();
    setResetLoading(true);
    setResetError('');
    setResetMessage('');

    if (!resetEmail || !resetNewPassword) {
      setResetError('Please enter both your email address and a new password.');
      setResetLoading(false);
      return;
    }

    if (resetNewPassword?.length < 8) {
      setResetError('New password must be at least 8 characters long.');
      setResetLoading(false);
      return;
    }

    try {
      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL || 'https://ttlptzwwghqbnlnbcpth.supabase.co';
      const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(`${supabaseUrl}/functions/v1/admin-reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({
          email: resetEmail,
          new_password: resetNewPassword,
        }),
      });

      const data = await response?.json()?.catch(() => ({}));

      if (!response?.ok) {
        const errMsg = data?.error || `Request failed with status ${response?.status}`;
        throw new Error(errMsg);
      }

      setResetMessage(`Password has been reset successfully for ${resetEmail}. You can now sign in with your new password.`);
      setResetNewPassword('');
    } catch (err) {
      console.error('Password reset error:', err);
      const msg = err?.message || '';
      if (msg?.includes('Unauthorized') || msg?.includes('401')) {
        setResetError('Password reset is restricted to authorised accounts. Please contact support if you need assistance.');
      } else if (msg?.includes('User not found') || msg?.includes('404')) {
        setResetError('No account found with that email address. Please check and try again.');
      } else if (msg?.includes('Failed to fetch') || msg?.includes('NetworkError')) {
        setResetError('Unable to reach the server. Please check your connection and try again.');
      } else {
        setResetError(msg || 'Failed to reset password. Please try again.');
      }
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to your account
          </h2>
        </div>

        {/* Forgot Password Modal */}
        {showForgotPassword && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Reset your password</h3>
                <button
                  onClick={() => { setShowForgotPassword(false); setResetMessage(''); setResetError(''); setResetEmail(''); setResetNewPassword(''); }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>

              {!resetMessage ? (
                <>
                  <p className="text-sm text-gray-600 mb-4">
                    Enter your email address and choose a new password. Your password will be updated immediately via the secure admin reset service.
                  </p>
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <Input
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e?.target?.value)}
                      placeholder="Enter your email address"
                      required
                    />
                    <Input
                      type="password"
                      value={resetNewPassword}
                      onChange={(e) => setResetNewPassword(e?.target?.value)}
                      placeholder="Enter your new password (min. 8 characters)"
                      required
                      minLength={8}
                    />
                    {resetError && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-3 rounded-lg text-sm">
                        {resetError}
                      </div>
                    )}
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => { setShowForgotPassword(false); setResetError(''); setResetEmail(''); setResetNewPassword(''); }}
                        className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                      >
                        Cancel
                      </button>
                      <Button
                        type="submit"
                        disabled={resetLoading || !resetEmail || !resetNewPassword}
                        className="flex-1"
                      >
                        {resetLoading ? 'Resetting...' : 'Reset Password'}
                      </Button>
                    </div>
                  </form>
                </>
              ) : (
                <div className="text-center">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-700 mb-4">{resetMessage}</p>
                  <button
                    onClick={() => { setShowForgotPassword(false); setResetMessage(''); setResetEmail(''); setResetNewPassword(''); }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    Back to Sign In
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className={`border px-4 py-3 rounded-lg ${
              error === 'SUPABASE_PAUSED' ? 'bg-amber-50 border-amber-300 text-amber-900' :
              error === 'SCHEMA_ERROR'? 'bg-red-50 border-red-300 text-red-900' : 'bg-red-50 border-red-200 text-red-700'
            }`}>
              <div className="flex items-start">
                <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  {error === 'SUPABASE_PAUSED' ? (
                    <>
                      <p className="font-semibold mb-1">Supabase Project Paused</p>
                      <p className="text-sm mb-2">Your Supabase project is currently unavailable. This typically happens when a free-tier project pauses after 7 days of inactivity.</p>
                      <ol className="text-sm list-decimal list-inside space-y-1 mb-3">
                        <li>Visit your <strong>Supabase Dashboard</strong></li>
                        <li>Find your project and click <strong>"Restore project"</strong></li>
                        <li>Wait ~1 minute for it to resume, then try signing in again</li>
                      </ol>
                      <a
                        href="https://supabase.com/dashboard"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block text-sm bg-amber-600 text-white px-4 py-2 rounded hover:bg-amber-700 transition-colors font-medium"
                      >
                        Open Supabase Dashboard →
                      </a>
                    </>
                  ) : error === 'SCHEMA_ERROR' ? (
                    <>
                      <p className="font-semibold mb-2 text-base">⚠️ Supabase Schema Error — Action Required</p>
                      <p className="text-sm mb-3">Supabase is returning <code className="bg-red-100 px-1 rounded font-mono">Database error querying schema</code>. This is a <strong>Supabase project configuration issue</strong> — your credentials are correct but the database role search_path is broken.</p>
                      <p className="text-sm font-semibold mb-2">Fix in 3 steps:</p>
                      <ol className="text-sm list-decimal list-inside space-y-2 mb-3">
                        <li>Open <strong>Supabase Dashboard → SQL Editor</strong> (link below)</li>
                        <li>Paste and run this exact SQL:
                          <div className="mt-1 bg-red-100 border border-red-300 rounded p-2 font-mono text-xs break-all select-all">
                            ALTER ROLE authenticator SET search_path = "$user", public, auth;
                          </div>
                        </li>
                        <li>Go to <strong>Settings → General → Restart project</strong>, wait ~60 seconds, then sign in again</li>
                      </ol>
                      <div className="flex gap-2 flex-wrap">
                        <a
                          href="https://supabase.com/dashboard/project/ttlptzwwghqbnlnbcpth/sql/new"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block text-sm bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors font-medium"
                        >
                          Open SQL Editor →
                        </a>
                        <a
                          href="https://supabase.com/dashboard/project/ttlptzwwghqbnlnbcpth/settings/general"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block text-sm bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition-colors font-medium"
                        >
                          Project Settings →
                        </a>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="font-semibold mb-1">Sign In Failed</p>
                      <p className="text-sm whitespace-pre-line">{error}</p>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {connectionStatus && (
            <div className={`border px-4 py-3 rounded-lg ${
              connectionStatus?.status === 'connected' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-yellow-50 border-yellow-200 text-yellow-700'
            }`}>
              <p className="font-semibold mb-1">{connectionStatus?.message}</p>
              {connectionStatus?.details && (
                <p className="text-sm mt-1">{connectionStatus?.details}</p>
              )}
              {connectionStatus?.action && (
                <p className="text-sm mt-2 font-medium">{connectionStatus?.action}</p>
              )}
            </div>
          )}

          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e?.target?.value)}
                placeholder="Email address"
                required
                className="rounded-t-md"
              />
            </div>
            <div>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e?.target?.value)}
                placeholder="Password"
                required
                className="rounded-b-md"
              />
            </div>
          </div>

          <div className="flex items-center justify-end">
            <button
              type="button"
              onClick={() => { setShowForgotPassword(true); setResetEmail(email); }}
              className="text-sm font-medium text-blue-600 hover:text-blue-500 transition-colors"
            >
              Forgot password?
            </button>
          </div>

          <div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </Button>
          </div>

          <div className="text-sm text-center">
            <Link to="/signup" className="font-medium text-blue-600 hover:text-blue-500">
              Don't have an account? Sign up
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}